// @ts-strict-ignore
import { Box } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { analyticsMessages } from "../../messages";
import { FunnelStage } from "../../models/analytics";
import { EmptyState } from "../EmptyState";
import { seriesColors, surfaceCardStyle } from "./tokens";

interface FunnelTileProps {
  title: string;
  description?: string;
  stages: FunnelStage[];
  /** Optional per-stage prior-period delta (0..1 relative change). */
  stageDeltas?: Record<string, number | null>;
  loading?: boolean;
  na?: boolean;
  emptyMessage?: string;
}

export const FunnelTile: React.FC<FunnelTileProps> = ({
  title,
  description,
  stages,
  stageDeltas,
  loading,
  na,
  emptyMessage,
}) => {
  const intl = useIntl();
  const empty = !na && !loading && (!stages || stages.length === 0 || stages.every(s => !s.count));
  const top = Math.max(1, ...(stages || []).map(s => s.count));

  return (
    <Box padding={5} style={{ ...surfaceCardStyle }}>
      <Box marginBottom={4}>
        <Box as="div" fontSize={2} color="default2">
          {title}
        </Box>
        {description && (
          <Box as="div" fontSize={1} color="default2" marginTop={1} style={{ opacity: 0.8 }}>
            {description}
          </Box>
        )}
      </Box>

      {na ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonNA)}
        </Box>
      ) : loading ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonLoading)}
        </Box>
      ) : empty ? (
        <EmptyState message={emptyMessage ?? intl.formatMessage(analyticsMessages.commonNoData)} compact />
      ) : (
        <Box display="flex" flexDirection="column" gap={3}>
          {stages.map((s, i) => {
            const widthPct = (s.count / top) * 100;
            const prev = i > 0 ? stages[i - 1].count : null;
            const stepDrop = prev != null && prev > 0 ? 1 - s.count / prev : null;
            const overallConv =
              stages[0].count > 0 && i > 0 ? s.count / stages[0].count : i === 0 ? 1 : 0;
            const color = seriesColors[i % seriesColors.length];

            return (
              <Box key={s.key} display="flex" flexDirection="column" gap={1}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={3}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      as="span"
                      __width={20}
                      __height={20}
                      borderRadius={2}
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      style={{
                        background: `${color}22`,
                        color,
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Box
                      as="span"
                      style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}
                    >
                      {s.label}
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="baseline" gap={3}>
                    <Box
                      as="span"
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#111827",
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {s.count.toLocaleString()}
                    </Box>
                    {(() => {
                      const d = stageDeltas?.[s.key];

                      if (d == null || !isFinite(d)) return null;

                      const up = d > 0;
                      const flat = Math.abs(d) < 0.001;
                      const c = flat ? "#6B7280" : up ? "#059669" : "#DC2626";

                      return (
                        <Box
                          as="span"
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: c,
                            fontFeatureSettings: '"tnum"',
                          }}
                        >
                          {flat ? "—" : up ? "▲" : "▼"} {Math.abs(d * 100).toFixed(1)}% vs prev
                        </Box>
                      );
                    })()}
                    {i > 0 && (
                      <Box as="span" style={{ fontSize: 11, color: "#6B7280" }}>
                        {(overallConv * 100).toFixed(1)}% overall
                        {stepDrop != null && stepDrop > 0 && (
                          <span style={{ color: "#DC2626", marginLeft: 8 }}>
                            ▼ {(stepDrop * 100).toFixed(1)}%
                          </span>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box
                  __height={10}
                  borderRadius={1}
                  style={{
                    background: "rgba(15, 23, 42, 0.05)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    __height={10}
                    style={{
                      width: `${widthPct}%`,
                      background: `linear-gradient(90deg, ${color}, ${color}cc)`,
                      borderRadius: 4,
                      transition: "width 240ms ease",
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
